/**
 * Filter helpers for the Audience Segmentation dashboard.
 *
 * Sections in this file:
 *   1. Default state
 *   2. Static labels (completeness fields, days, months)
 *   3. Casing helpers (title-case, "Other" bucket recasing)
 *   4. Option builders (aggregate counts -> dropdown options)
 *   5. Derived "Other" counts (display-casing for the live aggregate)
 *   6. Filter params (localFilters -> GraphQL/REST query params)
 *   7. Active filter count / pills (for the filter drawer's summary UI)
 *   8. Misc display helpers (filter summary text, colour alpha)
 */

import {
  SEGMENT_ORDER,
  SEGMENT_LABELS,
  getSegmentColour,
  getSegmentIcon,
  getChannelName,
  formatNumber,
} from './segmentation';

// ============================================================
// 1. Default state
// ============================================================

/** Empty/unfiltered shape of localFilters — every filter section's v-model starts here. */
export const DEFAULT_FILTERS = {
  segments: [],
  genders: [],
  ageGroups: [],
  languages: [],
  locations: [],
  ethnicities: [],
  tags: [],
  channels: [],
  lsmTiers: [],
  campaignTypes: [],
  peakTimesOfDay: [],
  daysOfWeek: [],
  months: [],
  weekdayPattern: null,
  missingFields: [],
  publicHolidays: [],
  timeslots: [],
  interests: [],
  interestsAny: [],
  intentCategories: [],
  intentTier: '',
  optIn: false,
  permission: false,
  originator: '',
  // A saved audience whose members are listed; the name is display-only.
  audienceId: '',
  audienceName: '',
  profileComplete: null,
  enriched: null,
  linkedStatus: '',
  linkedTypes: [],
};

// ============================================================
// 2. Static labels
// ============================================================

/** Display labels for the profile-completeness field list (matches backend COMPLETENESS_FIELDS). */
export const COMPLETENESS_FIELD_LABELS = [
  'Mobile Number',
  'Gender',
  'Age',
  'Location',
  'Language',
  'Ethnicity',
  'Name',
  'Email Address',
  'ID Number',
];

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * @param {number} idx  0-6 (primary_day_of_week)
 * @returns {string}    e.g. "Monday", or "—" if out of range
 */
export const dayName = idx => DAYS[idx] || '—';

/**
 * @param {number} idx  1-12 (peak_month)
 * @returns {string}    e.g. "Jan", or "—" if out of range
 */
export const monthName = idx => MONTHS[(idx || 1) - 1] || '—';

// ============================================================
// 3. Casing helpers
// ============================================================

/**
 * Fields whose aggregate has a genuine "other" bucket (a catch-all for
 * values outside a small known set) that needs consistent display
 * casing — see aggregate_formatter.py on the backend for the matching
 * bucket logic. age_group is handled the same way but kept as its own
 * explicit line in deriveScopedCounts() rather than listed here.
 */
const OTHER_BUCKET_FIELDS = [
  'genderCounts',
  'languageCounts',
  'locationCounts',
  'ethnicityCounts',
  'inferredLsmCounts',
];

/**
 * @param {string} str
 * @returns {string}  Title Case, e.g. "johannesburg" -> "Johannesburg"
 */
export const toTitleCase = str =>
  str
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/**
 * Title-cases every key in a counts map, merging any keys that collide
 * after casing (and treating a blank/whitespace key as "Other").
 * @param {Object} counts  raw counts keyed by lowercase value, e.g. {male: 4}
 * @returns {Object}       same counts, keyed by display-cased value
 */
export const recaseCounts = counts => {
  if (!counts) {
    return {};
  }
  const result = {};
  Object.entries(counts).forEach(([k, v]) => {
    const key = k && k.trim() ? toTitleCase(k.trim()) : 'Other';
    result[key] = (result[key] || 0) + v;
  });
  return result;
};

// ============================================================
// 4. Option builders
// ============================================================

/**
 * Builds a sorted, de-duplicated list of selectable option labels from
 * a raw counts map. "Other" (any casing) always sorts last.
 * @param {Object} counts  raw counts, e.g. {male: 4, other: 6}
 * @param {boolean} cap    title-case non-"other" values (used for free-text
 *                         fields like gender/location; left false for
 *                         fields whose values are already well-formed,
 *                         like segment keys or campaign types)
 * @returns {string[]}
 */
const fromCounts = (counts, cap = false) => {
  if (!counts) {
    return [];
  }
  return Object.entries(counts)
    .filter(([k, v]) => k && v > 0)
    .map(([k]) => {
      const s = k.trim();
      if (!s) {
        return null;
      }
      if (s.toLowerCase() === 'other') {
        return 'Other';
      }
      return cap ? toTitleCase(s) : s;
    })
    .filter(v => v !== null)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => {
      const aIsOther = a.toLowerCase() === 'other';
      const bIsOther = b.toLowerCase() === 'other';
      if (aIsOther) {
        return 1;
      }
      if (bIsOther) {
        return -1;
      }
      return a.localeCompare(b);
    });
};

/**
 * timeslot_counts is {id: {title, count}}, not {id: count} like every
 * other facet — needs its own builder rather than fromCounts.
 *
 * Note: Id 0 is "Other", the listeners with no timeslot. It always goes last,
 * whatever its count.
 * @param {Object} timeslotCounts
 * @returns {Array<{id: number, title: string, count: number}>}
 */
const fromTimeslotCounts = timeslotCounts => {
  if (!timeslotCounts) {
    return [];
  }
  return Object.entries(timeslotCounts)
    .map(([id, data]) => ({
      id: parseInt(id, 10),
      title: data.title,
      count: data.count,
    }))
    .sort((a, b) => {
      if (a.id === 0 || b.id === 0) {
        return a.id === 0 ? 1 : -1;
      }
      return b.count - a.count;
    });
};

/**
 * Builds every filter section's dropdown options from one aggregate
 * response. Called once whenever the unfiltered ("base") aggregate
 * query resolves.
 * @param {Object} agg  baseAggregate from the container
 * @returns {Object}    options keyed by filter group, e.g. {genders: [...], ...}
 */
export const buildFilterOptions = agg => ({
  genders: fromCounts(agg.genderCounts, true),
  ageGroups: fromCounts(agg.ageGroupCounts, false),
  languages: fromCounts(agg.languageCounts, true),
  locations: fromCounts(agg.locationCounts, true),
  ethnicities: fromCounts(agg.ethnicityCounts, true),
  tags: fromCounts(agg.tagCounts, false),
  campaignTypes: fromCounts(agg.campaignTypeCounts, false),
  peakTimesOfDay: fromCounts(agg.peakTimeOfDayCounts, true),
  publicHolidays: fromCounts(agg.publicHolidayCounts, false),
  timeslots: fromTimeslotCounts(agg.timeslotCounts),
  lsmTiers: fromCounts(agg.inferredLsmCounts, true),
  channels: Object.entries(agg.channelCounts || {})
    .filter(([, v]) => v > 0)
    .map(([k]) => parseInt(k, 10))
    .filter(n => !isNaN(n)),
  daysOfWeek: Object.entries(agg.primaryDayOfWeekCounts || {})
    .filter(([, v]) => v > 0)
    .map(([k]) => parseInt(k, 10))
    .filter(n => !isNaN(n))
    .sort((a, b) => a - b),
  months: Object.entries(agg.peakMonthCounts || {})
    .filter(([, v]) => v > 0)
    .map(([k]) => parseInt(k, 10))
    .filter(n => !isNaN(n))
    .sort((a, b) => a - b),
});

/**
 * Builds the ordered segment list (with counts) for the segment
 * filter/summary, in SEGMENT_ORDER rather than count order.
 * @param {Object} segmentCounts  from summary.segmentCounts
 * @returns {Array<{key, count, label, colour, icon}>}
 */
export const buildAvailableSegments = segmentCounts =>
  SEGMENT_ORDER.map(key => ({
    key,
    count: segmentCounts[key] || 0,
    label: SEGMENT_LABELS[key] || key,
    colour: getSegmentColour(key),
    icon: getSegmentIcon(key),
  }));

// ============================================================
// 5. Derived "Other" counts
// ============================================================

/**
 * Re-cases a *live, filter-scoped* aggregate response so its "Other"
 * buckets display consistently with the option labels built from the
 * base aggregate (buildFilterOptions/fromCounts). Needed because the
 * raw GraphQL response is always lowercase (e.g. "male", "other").
 * @param {Object} agg  raw aggregate from the live, filter-scoped query
 * @returns {Object}    same aggregate with display-cased buckets
 */
export const deriveScopedCounts = agg => {
  if (!agg) {
    return agg;
  }
  const result = { ...agg };
  OTHER_BUCKET_FIELDS.forEach(field => {
    result[field] = recaseCounts(agg[field]);
  });
  if (agg.ageGroupCounts) {
    result.ageGroupCounts = recaseCounts(agg.ageGroupCounts);
  }
  return result;
};

/**
 * Narrows a list of options down to only those with a non-zero count —
 * falls back to the full list if that would leave nothing to show.
 * @param {Array} options
 * @param {Object} counts
 * @returns {Array}
 */
export const visibleOptions = (options, counts) => {
  if (!counts) {
    return options;
  }
  const filtered = options.filter(o => (counts[o] || 0) > 0);
  return filtered.length > 0 ? filtered : options;
};

/**
 * Same idea as visibleOptions, but always narrows (no fallback) — used
 * where showing a stale option with a genuine zero count would be
 * actively misleading (e.g. active-pill chips).
 * @param {string[]} options
 * @param {Object}   counts
 * @returns {string[]}
 */
export const filterNonZero = (options, counts) => {
  if (!counts) {
    return options;
  }
  return options.filter(o => (counts[o] || 0) > 0);
};

// ============================================================
// 6. Filter params
// ============================================================

/**
 * Declarative config for buildFilterParams: most filters just need a
 * query param name and an optional lowercase transform. Entries
 * needing a different shape (single-vs-list, comma-join, booleans,
 * nullable scalars) are handled separately below the loop, since
 * forcing them into this table would obscure their real shape.
 */
const LIST_PARAM_MAP = [
  { field: 'genders', param: 'gender', lower: true },
  { field: 'ageGroups', param: 'ageGroup' },
  { field: 'languages', param: 'language', lower: true },
  { field: 'locations', param: 'location', lower: true },
  { field: 'ethnicities', param: 'ethnicity', lower: true },
  { field: 'lsmTiers', param: 'inferredLsm', lower: true },
  { field: 'channels', param: 'channelType' },
  { field: 'campaignTypes', param: 'campaignType' },
  { field: 'peakTimesOfDay', param: 'peakTimeOfDay', lower: true },
  { field: 'daysOfWeek', param: 'primaryDayOfWeek' },
  { field: 'months', param: 'peakMonth' },
  { field: 'missingFields', param: 'missingFields' },
  { field: 'publicHolidays', param: 'publicHoliday' },
  { field: 'timeslots', param: 'timeslotId' },
  { field: 'intentCategories', param: 'intentCategory' },
];

/**
 * Converts the dashboard's localFilters (display-cased, e.g. "Male",
 * "Lower-Middle") into the query params the backend expects (mostly
 * lowercase, since that's how values are stored in Mongo).
 * @param {Object} filters  localFilters from Dashboard data
 * @returns {Object}        params to emit to the container
 */
export const buildFilterParams = filters => {
  const p = {};

  LIST_PARAM_MAP.forEach(({ field, param, lower }) => {
    const values = filters[field];
    if (values && values.length > 0) {
      p[param] = lower ? values.map(v => v.toLowerCase()) : values;
    }
  });

  // segment: single value when exactly one selected, list otherwise
  if (filters.segments.length > 0) {
    p.segment = filters.segments.length === 1 ? filters.segments[0] : filters.segments;
  }

  // tags: comma-joined string, not an array
  if (filters.tags.length > 0) {
    p.tags = filters.tags.join(',');
  }

  // interests: comma-joined strings — all of / any of
  if (filters.interests && filters.interests.length > 0) {
    p.inferredInterests = filters.interests.join(',');
  }
  if (filters.interestsAny && filters.interestsAny.length > 0) {
    p.interestsAny = filters.interestsAny.join(',');
  }

  if (filters.intentTier) {
    p.intentTier = filters.intentTier;
  }

  // booleans and nullable scalars — no list logic applies
  if (filters.weekdayPattern !== null && filters.weekdayPattern !== undefined) {
    p.weekdayPattern = filters.weekdayPattern;
  }
  if (filters.optIn) {
    p.optIn = true;
  }
  if (filters.permission) {
    p.permission = true;
  }
  if (filters.originator) {
    p.originator = filters.originator;
  }
  if (filters.audienceId) {
    p.audienceId = filters.audienceId;
  }
  if (filters.profileComplete !== null && filters.profileComplete !== undefined) {
    p.profileComplete = filters.profileComplete;
  }
  if (filters.enriched !== null && filters.enriched !== undefined) {
    p.enriched = filters.enriched;
  }
  if (filters.linkedStatus) {
    p.linkedStatus = filters.linkedStatus;
  }
  if ((filters.linkedTypes || []).length) {
    p.linkedType = filters.linkedTypes;
  }

  return p;
};

/**
 * Turns the search-filters API result (snake_case, lowercase values) into
 * a complete localFilters object. A search starts from a clean slate —
 * it replaces whatever was selected before.
 * @param {Object} ai  `filters` from the searchFilters mutation
 * @returns {Object}   localFilters
 */
export const filtersFromAiSearch = (ai = {}) => ({
  ...DEFAULT_FILTERS,
  segments: ai.segment || [],
  genders: (ai.gender || []).map(toTitleCase),
  ageGroups: (ai.age_group || []).map(toTitleCase),
  ethnicities: (ai.ethnicity || []).map(toTitleCase),
  languages: (ai.language || []).map(toTitleCase),
  locations: (ai.location || []).map(toTitleCase),
  channels: ai.channel_type || [],
  interests: ai.inferred_interests || [],
  interestsAny: ai.interests_any || [],
  optIn: ai.opt_in === true,
  permission: ai.permission === true,
});

// ============================================================
// 7. Active filter count / pills
// ============================================================

/**
 * @param {number} id
 * @param {Array} timeslotOptions  options.timeslots
 * @returns {string}
 */
export const timeslotTitle = (id, timeslotOptions) => {
  const match = (timeslotOptions || []).find(t => t.id === id);
  if (match) {
    return match.title;
  }
  return id === 0 ? 'Other' : `Timeslot ${id}`;
};

/**
 * Total number of individually active filter selections, across every
 * group — drives the "N filters active" badge on the filter drawer.
 * @param {Object} filters  localFilters
 * @returns {number}
 */
export const countActiveFilters = filters =>
  filters.segments.length +
  filters.genders.length +
  filters.ageGroups.length +
  filters.languages.length +
  filters.locations.length +
  filters.ethnicities.length +
  filters.tags.length +
  filters.channels.length +
  filters.lsmTiers.length +
  filters.campaignTypes.length +
  filters.peakTimesOfDay.length +
  filters.daysOfWeek.length +
  filters.months.length +
  filters.missingFields.length +
  filters.publicHolidays.length +
  filters.timeslots.length +
  (filters.interests || []).length +
  (filters.interestsAny || []).length +
  (filters.intentCategories || []).length +
  (filters.intentTier ? 1 : 0) +
  (filters.weekdayPattern !== null && filters.weekdayPattern !== undefined ? 1 : 0) +
  (filters.optIn ? 1 : 0) +
  (filters.permission ? 1 : 0) +
  (filters.originator ? 1 : 0) +
  (filters.audienceId ? 1 : 0) +
  (filters.profileComplete !== null && filters.profileComplete !== undefined ? 1 : 0) +
  (filters.enriched !== null && filters.enriched !== undefined ? 1 : 0) +
  (filters.linkedStatus ? 1 : 0) +
  (filters.linkedTypes || []).length;

/**
 * Builds the dismissible "active filter" chips shown above the listener
 * grid — one entry per individual selection, keyed so clearPill() can
 * remove exactly that one selection later.
 * @param {Object} filters  localFilters
 * @param {Array} timeslotOptions  options.timeslots, for title lookup
 * @returns {Object}        { key: label }
 */
export const buildActivePills = (filters, timeslotOptions = []) => {
  const pills = {};

  filters.segments.forEach(s => {
    pills[`seg_${s}`] = `Segment: ${SEGMENT_LABELS[s] || s}`;
  });
  filters.genders.forEach(g => {
    pills[`gen_${g}`] = `Gender: ${g}`;
  });
  filters.ageGroups.forEach(a => {
    pills[`age_${a}`] = `Age: ${a}`;
  });
  filters.languages.forEach(l => {
    pills[`lang_${l}`] = `Language: ${l}`;
  });
  filters.locations.forEach(l => {
    pills[`loc_${l}`] = `Location: ${l}`;
  });
  filters.ethnicities.forEach(e => {
    pills[`eth_${e}`] = `Ethnicity: ${e}`;
  });
  filters.tags.forEach(t => {
    pills[`tag_${t}`] = `Tag: ${t.replace(/_/g, ' ')}`;
  });
  filters.channels.forEach(c => {
    pills[`chan_${c}`] = `Channel: ${getChannelName(c)}`;
  });
  filters.lsmTiers.forEach(l => {
    pills[`lsm_${l}`] = `LSM: ${l}`;
  });
  filters.campaignTypes.forEach(c => {
    pills[`camp_${c}`] = `Campaign: ${c.replace(/_/g, ' ')}`;
  });
  filters.peakTimesOfDay.forEach(t => {
    pills[`ptod_${t}`] = `Peak Time: ${t}`;
  });
  filters.daysOfWeek.forEach(d => {
    pills[`day_${d}`] = `Day: ${dayName(d)}`;
  });
  filters.months.forEach(m => {
    pills[`month_${m}`] = `Month: ${monthName(m)}`;
  });
  filters.missingFields.forEach(f => {
    pills[`miss_${f}`] = `Missing: ${f}`;
  });
  filters.publicHolidays.forEach(h => {
    pills[`hol_${h}`] = `Holiday: ${h}`;
  });
  filters.timeslots.forEach(id => {
    pills[`ts_${id}`] = `Timeslot: ${timeslotTitle(id, timeslotOptions)}`;
  });
  (filters.interests || []).forEach(i => {
    pills[`int_${i}`] = `Interest (all): ${i}`;
  });
  (filters.interestsAny || []).forEach(i => {
    pills[`inta_${i}`] = `Interest (any): ${i}`;
  });
  (filters.intentCategories || []).forEach(c => {
    pills[`intc_${c}`] = `Intent in: ${c}`;
  });
  if (filters.intentTier) {
    pills.intentTier = `Intent: ${filters.intentTier} or higher`;
  }

  if (filters.weekdayPattern !== null && filters.weekdayPattern !== undefined) {
    pills.weekdayPattern = filters.weekdayPattern ? 'Weekday listener' : 'Weekend listener';
  }
  if (filters.optIn) {
    pills.optIn = 'Opted in';
  }
  if (filters.permission) {
    pills.permission = 'Permission granted';
  }
  if (filters.originator) {
    pills.originator = `Search: ${filters.originator}`;
  }
  if (filters.audienceId) {
    pills.audience = `Audience: ${filters.audienceName || 'saved audience'}`;
  }
  if (filters.profileComplete !== null && filters.profileComplete !== undefined) {
    pills.profileComplete = filters.profileComplete ? 'Complete profiles' : 'Incomplete profiles';
  }
  if (filters.linkedStatus) {
    const text = {
      any: 'Has linked identifiers',
      unreviewed: 'Linked, not reviewed',
      confirmed: 'Linked, confirmed',
    };
    pills.linkedStatus = text[filters.linkedStatus] || 'Linked identifiers';
  }
  (filters.linkedTypes || []).forEach(t => {
    pills[`lnk_${t}`] = `Linked: ${t.replace(/_handle$/, '').replace(/_/g, ' ')}`;
  });
  if (filters.enriched !== null && filters.enriched !== undefined) {
    pills.enriched = filters.enriched ? 'AI enriched' : 'Needs AI enrichment';
  }

  return pills;
};

/**
 * Removes exactly one active-pill selection (by its buildActivePills
 * key) from localFilters, returning a new filters object.
 * @param {Object} filters  current localFilters
 * @param {string} key      pill key, e.g. 'gen_Male'
 * @returns {Object}        updated filters
 */
export const clearPill = (filters, key) => {
  const f = { ...filters };

  const prefixMap = {
    seg_: 'segments',
    gen_: 'genders',
    age_: 'ageGroups',
    lang_: 'languages',
    loc_: 'locations',
    eth_: 'ethnicities',
    tag_: 'tags',
    chan_: 'channels',
    lsm_: 'lsmTiers',
    camp_: 'campaignTypes',
    ptod_: 'peakTimesOfDay',
    day_: 'daysOfWeek',
    month_: 'months',
    miss_: 'missingFields',
    hol_: 'publicHolidays',
    ts_: 'timeslots',
    int_: 'interests',
    inta_: 'interestsAny',
    intc_: 'intentCategories',
    lnk_: 'linkedTypes',
  };

  const match = Object.entries(prefixMap).find(([prefix]) => key.startsWith(prefix));
  if (match) {
    const [prefix, field] = match;
    const raw = key.slice(prefix.length);
    const isNumericField = ['channels', 'daysOfWeek', 'months', 'timeslots'].includes(field);
    f[field] = f[field].filter(v => {
      if (isNumericField) {
        return Number(v) !== Number(raw);
      }
      return `${prefix}${v}` !== key;
    });
    return f;
  }

  if (key === 'intentTier') {
    f.intentTier = '';
  }
  if (key === 'weekdayPattern') {
    f.weekdayPattern = null;
  }
  if (key === 'optIn') {
    f.optIn = false;
  }
  if (key === 'permission') {
    f.permission = false;
  }
  if (key === 'originator') {
    f.originator = '';
  }
  if (key === 'audience') {
    f.audienceId = '';
    f.audienceName = '';
  }
  if (key === 'profileComplete') {
    f.profileComplete = null;
  }
  if (key === 'enriched') {
    f.enriched = null;
  }
  if (key === 'linkedStatus') {
    f.linkedStatus = '';
  }

  return f;
};

// ============================================================
// 8. Misc display helpers
// ============================================================

/**
 * Builds a short, readable English summary of active filters for the
 * AI campaign brief. Not exhaustive — caps at a few notable filters
 * plus a "+N more" tail, since the full structured filters are sent
 * to the backend separately via activeFilters regardless of this text.
 * @param {Object} filters  localFilters
 * @returns {string}
 */
export const buildFilterSummary = filters => {
  const parts = [];

  if (filters.segments.length) {
    parts.push(`${filters.segments.map(s => SEGMENT_LABELS[s] || s).join('/')} listeners`);
  }
  if (filters.genders.length) {
    parts.push(filters.genders.join(' and '));
  }
  if (filters.ageGroups.length) {
    parts.push(`aged ${filters.ageGroups.join(', ')}`);
  }
  if (filters.locations.length) {
    parts.push(`in ${filters.locations.slice(0, 2).join(', ')}`);
  }
  if (filters.languages.length) {
    parts.push(`speaking ${filters.languages.join(', ')}`);
  }
  if (filters.ethnicities.length) {
    parts.push(filters.ethnicities.join(', '));
  }
  if (filters.campaignTypes.length) {
    parts.push(
      `engaging with ${filters.campaignTypes.map(c => c.replace(/_/g, ' ')).join(', ')} campaigns`,
    );
  }
  if ((filters.interestsAny || []).length) {
    parts.push(`interested in ${filters.interestsAny.slice(0, 3).join(' or ')}`);
  }
  if ((filters.interests || []).length) {
    parts.push(`interested in ${filters.interests.slice(0, 3).join(' and ')}`);
  }
  if (filters.tags.length) {
    parts.push(
      `tagged ${filters.tags
        .slice(0, 2)
        .map(t => t.replace(/_/g, ' '))
        .join(', ')}`,
    );
  }
  if (filters.peakTimesOfDay.length) {
    parts.push(`peak ${filters.peakTimesOfDay.map(t => t.replace(/_/g, ' ')).join('/')}`);
  }
  if (filters.daysOfWeek.length) {
    parts.push(`favouring ${filters.daysOfWeek.map(dayName).join(', ')}`);
  }
  if (filters.weekdayPattern !== null && filters.weekdayPattern !== undefined) {
    parts.push(filters.weekdayPattern ? 'weekday listeners' : 'weekend listeners');
  }
  if (filters.publicHolidays.length) {
    parts.push(`active on ${filters.publicHolidays.slice(0, 1).join(', ')}`);
  }
  if (filters.optIn) {
    parts.push('opted in');
  }
  if (filters.permission) {
    parts.push('with permission granted');
  }

  const total = countActiveFilters(filters);
  const shown = parts.length;
  const capped = parts.slice(0, 5);
  const remaining = total - Math.min(shown, 5);

  let summary = capped.join(', ');
  if (remaining > 0) {
    summary += ` (+${remaining} more filter${remaining > 1 ? 's' : ''})`;
  }

  return summary;
};

/**
 * @param {string} colour  e.g. 'rgb(104, 48, 144)'
 * @param {number} alpha   opacity 0-1
 * @returns {string}       the same colour as rgba(), e.g. 'rgba(104, 48, 144, 0.06)'
 */
export const rgbAlpha = (colour, alpha = 0.06) =>
  colour.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);

export { formatNumber, getChannelName };



// WEBPACK FOOTER //
// ./src/components/segmentation/utils/filterHelpers.js